// src/pages/Institutional/Contact.js
import React from 'react'
import { Container, Box, Typography, Paper, Grid, Divider } from '@mui/material'
import {
  WhatsApp,
  Email,
  Language,
  Speed,
  Description,
  Message
} from '@mui/icons-material'

const Contact = () => {
  const whatsAppUses = [
    'Dúvidas gerais',
    'Suporte rápido',
    'Informações sobre anúncios',
    'Contato comercial com imobiliárias',
    'Sugestões e melhorias'
  ]

  const emailUses = [
    'Parcerias',
    'Dúvidas específicas',
    'Suporte mais detalhado',
    'Solicitações de corretores e imobiliárias',
    'Envio de arquivos (XML, planilhas etc.)'
  ]

  const portalUses = [
    'Buscar imóveis',
    'Anunciar gratuitamente',
    'Entrar em contato com anunciantes',
    'Conhecer nossas páginas institucionais'
  ]

  const partnershipOptions = [
    'Publicar imóveis',
    'Integrar sistemas',
    'Solicitar informações técnicas',
    'Conhecer oportunidades comerciais'
  ]

  const contactChannels = [
    {
      icon: <WhatsApp />,
      iconColor: 'success.main',
      title: 'WhatsApp',
      subtitle: 'Atendimento rápido e direto',
      value: '(11) 9 8139-6930',
      description:
        'Ideal para suporte rápido, comunicação direta e respostas imediatas.',
      uses: whatsAppUses
    },
    {
      icon: <Email />,
      iconColor: 'primary.main',
      title: 'E-mail',
      subtitle: 'Para mensagens mais completas, documentos e propostas',
      value: 'contato@umarkt.com.br',
      description:
        'Indicamos esse canal para solicitações mais longas, parcerias, questões técnicas e envios de documentos.',
      uses: emailUses
    },
    {
      icon: <Language />,
      iconColor: 'info.main',
      title: 'Site',
      subtitle: 'Acesse nosso portal',
      value: 'www.umarkt.com.br',
      description:
        'No portal você pode buscar imóveis, anunciar gratuitamente e muito mais.',
      uses: portalUses
    }
  ]

  const whenToUse = [
    {
      icon: <WhatsApp />,
      channel: 'WhatsApp',
      description: 'Suporte rápido, comunicação direta e respostas imediatas.',
      color: 'success.main'
    },
    {
      icon: <Email />,
      channel: 'E-mail',
      description:
        'Solicitações mais longas, parcerias, questões técnicas e envios de documentos.',
      color: 'primary.main'
    },
    {
      icon: <Language />,
      channel: 'Portal',
      description:
        'Explorar imóveis, filtrar opções, publicar anúncios e acessar páginas informativas.',
      color: 'info.main'
    }
  ]

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
      <Container maxWidth='lg'>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant='h3'
            sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}
          >
            Contato – umarkt
          </Typography>
          <Typography
            variant='h6'
            sx={{
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Estamos sempre à disposição para ajudar, esclarecer dúvidas e
            construir novas parcerias.
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto',
              mt: 2,
              lineHeight: 1.6
            }}
          >
            Se você precisa falar com a nossa equipe, escolher uma forma de
            contato é simples.
          </Typography>
        </Box>

        {/* Canais de Contato */}
        <Grid container spacing={2} sx={{ mb: 6 }}>
          {contactChannels.map((channel, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'grey.200',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: channel.iconColor,
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {React.cloneElement(channel.icon, {
                    sx: { fontSize: '2.5rem', color: channel.iconColor }
                  })}
                </Box>
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    color: 'text.primary'
                  }}
                >
                  {channel.title}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}
                >
                  {channel.subtitle}
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    borderRadius: 2,
                    textAlign: 'center',
                    mb: 2
                  }}
                >
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 600,
                      color: channel.iconColor,
                      wordBreak: 'break-word'
                    }}
                  >
                    {channel.value}
                  </Typography>
                </Box>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.7 }}
                >
                  {channel.description}
                </Typography>
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Ideal para:
                </Typography>
                <Box component='ul' sx={{ pl: 2, m: 0 }}>
                  {channel.uses.map((use, idx) => (
                    <Typography
                      component='li'
                      key={idx}
                      variant='body2'
                      sx={{ color: 'text.secondary', mb: 0.5, lineHeight: 1.6 }}
                    >
                      {use}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 6 }} />

        {/* Quando usar cada canal */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              mb: 4,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Quando usar cada canal?
          </Typography>
          <Grid container spacing={3}>
            {whenToUse.map((item, index) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}
                key={index}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2
                    }}
                  >
                    {React.cloneElement(item.icon, {
                      sx: { fontSize: '2rem', color: item.color }
                    })}
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {item.channel}:
                    </Typography>
                  </Box>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                  >
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Retorno e Atendimento */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 3,
            mb: 6
          }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}
          >
            Retorno e Atendimento
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, lineHeight: 1.8, textAlign: 'center' }}
          >
            Buscamos responder todas as mensagens em:
          </Typography>
          <Grid container spacing={2} sx={{ maxWidth: '600px', mx: 'auto' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Speed sx={{ fontSize: '2rem', mb: 1 }} />
                <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
                  WhatsApp
                </Typography>
                <Typography variant='body2' sx={{ opacity: 0.9 }}>
                  Em poucos minutos (sempre que possível)
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Message sx={{ fontSize: '2rem', mb: 1 }} />
                <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
                  E-mail
                </Typography>
                <Typography variant='body2' sx={{ opacity: 0.9 }}>
                  Até 24 horas
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Parcerias e Imobiliárias */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'white',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Typography
            variant='h5'
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Parcerias e Imobiliárias
          </Typography>
          <Typography
            variant='body1'
            sx={{
              mb: 3,
              color: 'text.secondary',
              lineHeight: 1.8,
              textAlign: 'center'
            }}
          >
            Se você é corretor, imobiliária, ou trabalha com sistemas/CRM,
            também pode entrar em contato por estes canais para:
          </Typography>
          <Grid container spacing={2}>
            {partnershipOptions.map((option, index) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}
                key={index}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: 'primary.main'
                    }}
                  />
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    {option}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Mensagem Final */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography
            variant='h6'
            sx={{ color: 'text.secondary', fontWeight: 500 }}
          >
            Estamos aqui para ajudar!
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default Contact
